import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from './ThemeProvider';
import { toast } from 'sonner';
import { motion } from 'motion/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { Users, MessageCircle, AlertTriangle, TrendingUp, Eye, CheckCircle, XCircle, Menu, Search, Trash2, Filter, Calendar, ShieldCheck, Gavel, Coins, ArrowLeft, MoreHorizontal, UserX, UserCheck, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { ScrollArea, ScrollBar } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { UserService, ReportService, YapService, AdminService } from '../services/api';
import { ScaleSignalsService, ScaleSignals } from '../services/ScaleSignalsService';
import { useUserData } from './UserDataContext';

type DashboardCategory = 'Dashboard' | 'Economy' | 'Audit' | 'TotalUsers' | 'Safety' | 'Analytics';

  // Analytics data now dynamically generated within the component
const reportedContent = [
  {
    id: 1,
    type: 'yap',
    user: 'User123',
    avatar: 'https://images.unsplash.com/photo-1639575791011-df063c326e82?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxhZnJpY2FuJTIweW91dGglMjBwb3J0cmFpdHxlbnwxfHx8fDE3NjEzMzQ5OTV8MA&ixlib=rb-4.1.0&q=80&w=1080',
    content: 'Inappropriate language in post...',
    reports: 12,
    time: '15m ago',
    status: 'pending',
  },
  {
    id: 2,
    type: 'profile',
    user: 'User456',
    avatar: 'https://images.unsplash.com/photo-1655902585683-502d2317025b?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxhZnJpY2FuJTIwd29tYW4lMjBzbWlsaW5nfGVufDF8fHx8MTc2MTMzNDk5Nnww&ixlib=rb-4.1.0&q=80&w=1080',
    content: 'Suspicious profile activity',
    reports: 8,
    time: '1h ago',
    status: 'pending',
  },
  {
    id: 3,
    type: 'message',
    user: 'User789',
    avatar: 'https://images.unsplash.com/photo-1612720779828-8ba209f069ff?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxhZnJpY2FuJTIwbWFuJTIwdXJiYW58ZW58MXx8fHwxNzYxMzM0OTk2fDA&ixlib=rb-4.1.0&q=80&w=1080',
    content: 'Harassment reported in DM',
    reports: 5,
    time: '2h ago',
    status: 'pending',
  },
];

import { AnimatePresence } from 'motion/react';

interface ModeratorDashboardProps {
  isOpen: boolean;
  onClose: () => void;
}

const formatAuditDate = (timestamp: any) => {
  if (!timestamp) return 'Date Unknown';
  // Handle Firestore Timestamp
  if (typeof timestamp.toDate === 'function') {
    return timestamp.toDate().toLocaleString();
  }
  // Handle object with seconds (common in some Firebase versions/environments)
  if (timestamp && typeof timestamp === 'object' && 'seconds' in timestamp) {
    return new Date(timestamp.seconds * 1000).toLocaleString();
  }
  // Handle ISO string or Date object
  const date = new Date(timestamp);
  return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleString();
};

const formatUsername = (username: any) => {
  if (!username) return '';
  const clean = String(username).replace(/^@+/, '');
  return `@${clean}`;
};

const getModName = (log: any) => {
  if (log.actorUsername && log.actorUsername !== 'Unknown') return formatUsername(log.actorUsername);
  const id = log.actorId || log.moderatorId;
  if (id) return `Mod: ${String(id).slice(0, 6)}`;
  return 'System';
};

const processAnalytics = (allUsers: any[], allYaps: any[]) => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const today = new Date();
  
  const data = [];
  
  for (let i = 6; i >= 0; i--) {
    const endOfDay = new Date();
    endOfDay.setDate(today.getDate() - i);
    endOfDay.setHours(23, 59, 59, 999);
    
    const startOfDay = new Date(endOfDay);
    startOfDay.setHours(0,0,0,0);
    
    const dayName = i === 0 ? 'Today' : (i === 1 ? 'Yesterday' : days[endOfDay.getDay()]);

    const totalUsersOnDay = allUsers.filter((u: any) => {
      if (!u.createdAt) return true; 
      let d = u.createdAt?.toDate ? u.createdAt.toDate() : new Date(u.createdAt);
      return isNaN(d.getTime()) ? true : d <= endOfDay;
    }).length;

    const yapsOnDay = allYaps.filter((y: any) => {
      if (!y.createdAt) return false;
      let d = y.createdAt?.toDate ? y.createdAt.toDate() : new Date(y.createdAt);
      if (isNaN(d.getTime())) return false;
      return d >= startOfDay && d <= endOfDay;
    }).length;
    
    data.push({
      name: dayName,
      users: totalUsersOnDay,
      yaps: yapsOnDay,
      matches: Math.floor(yapsOnDay * 0.4) 
    });
  }
  
  return data;
};

export function ModeratorDashboard({ isOpen, onClose }: ModeratorDashboardProps) {
  // All hooks must be called unconditionally before any early return.
  const { userProfile } = useUserData();
  const { theme } = useTheme();
  const [activeCategory, setActiveCategory] = useState<DashboardCategory>('Dashboard');
  const [isUpdatingGold, setIsUpdatingGold] = useState(false);
  const [isCleaningUp, setIsCleaningUp] = useState(false);

  // Real Data State
  const [stats, setStats] = useState({
    totalUsers: '...',
    activeYaps: '...',
    pendingReports: '...',
    growthRate: '...'
  });
  const [realReports, setRealReports] = useState<any[]>([]);
  const [analyticsData, setAnalyticsData] = useState<any[]>([]);
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  // Selection state for Audit/Bulk Delete
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [moderationLogs, setModerationLogs] = useState<any[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);

  // Scale signals — the numbers behind the "needs data before deciding" calls on
  // community pricing, swipe pricing, and the New Yappers window.
  const [scaleSignals, setScaleSignals] = useState<ScaleSignals | null>(null);
  const [isLoadingSignals, setIsLoadingSignals] = useState(false);

  useEffect(() => {
    fetchDashboardStats();
    if (activeCategory === 'Audit') fetchLogs();
    if (activeCategory === 'TotalUsers') fetchUsersForAudit();
  }, [activeCategory]);

  // Rank guard — must be after all hooks. Renders nothing for non-moderators
  // regardless of how this component was opened (including console event dispatch).
  if (!userProfile || (userProfile.rank !== 'watcher' && userProfile.rank !== 'legion')) {
    return null;
  }

  async function fetchLogs() {
    setIsLoadingLogs(true);
    try {
      const logs = await AdminService.fetchModerationLogs(50);
      setModerationLogs(logs);
    } catch (error) {
      console.error("Failed to fetch logs:", error);
    } finally {
      setIsLoadingLogs(false);
    }
  }

  async function fetchDashboardStats() {
    setIsLoadingStats(true);
    try {
      const [users, reports, yaps] = await Promise.all([
        UserService.fetchAllUsers(),
        ReportService.fetchPendingReports(),
        YapService.fetchAllYaps()
      ]);

      const pendingCount = reports.success ? reports.reports?.length || 0 : 0;
      setStats({
        totalUsers: String(users.length),
        activeYaps: String(yaps.length),
        pendingReports: String(pendingCount),
        growthRate: 'N/A' // Calculated later
      });
      
      if (reports.success) {
        setRealReports(reports.reports || []);
      }

      setAnalyticsData(processAnalytics(users, yaps));

      // Scale signals reuse the users already in hand; only the group
      // collections cost an extra read, and only while Analytics is open.
      if (activeCategory === 'Analytics') {
        setIsLoadingSignals(true);
        ScaleSignalsService.compute(users)
          .then(setScaleSignals)
          .catch(e => console.error('Scale signals failed:', e))
          .finally(() => setIsLoadingSignals(false));
      }

    } catch (error) {
      console.error("Dashboard fetch error:", error);
    } finally {
      setIsLoadingStats(false);
    }
  }

  const handleUpdateReportStatus = async (reportId: string, status: 'resolved' | 'dismissed') => {
    try {
      const result = await ReportService.updateReportStatus(reportId, status);
      if (result.success) {
        // Log moderation action for audit trail
        try {
          const { getAuthSafe, getDbSafe } = await import('../services/firebaseConfig');
          const { collection, addDoc } = await import('firebase/firestore');
          const auth = await getAuthSafe();
          const db = await getDbSafe();
          const report = realReports.find((r: any) => r.id === reportId);
          await addDoc(collection(db, 'moderation_logs'), {
            moderatorId: auth.currentUser?.uid || 'unknown',
            actionType: status === 'resolved' ? 'content_removed' : 'report_dismissed',
            targetUserId: report?.reportedUserId || report?.targetId || 'unknown',
            reportId,
            timestamp: new Date().toISOString(),
            reason: `Report ${status} via dashboard`,
          });
        } catch (logErr) {
          // Non-blocking — log failure shouldn't affect the action
          console.error('Failed to write moderation log:', logErr);
        }
        toast.success(`Report ${status} successfully`);
        fetchDashboardStats(); // Refresh
      } else {
        toast.error(result.error || `Failed to ${status} report`);
      }
    } catch (error) {
      toast.error('An error occurred');
    }
  };

  const handleGrantGoldToAll = async () => {
    if (userProfile?.rank !== 'legion') { toast.error('Unauthorized'); return; }
    if (!window.confirm("Are you sure you want to give 5000 gold to EVERY user? This cannot be undone.")) return;
    
    setIsUpdatingGold(true);
    try {
      const { getDbSafe } = await import('../services/firebaseConfig');
      const { collection, getDocs, writeBatch, doc } = await import('firebase/firestore');
      
      const db = await getDbSafe();
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      
      if (snapshot.empty) {
        toast.error("No users found.");
        setIsUpdatingGold(false);
        return;
      }

      const BATCH_SIZE = 500;
      const chunks = [];
      const docs = snapshot.docs;

      for (let i = 0; i < docs.length; i += BATCH_SIZE) {
        chunks.push(docs.slice(i, i + BATCH_SIZE));
      }

      let updatedCount = 0;

      for (const chunk of chunks) {
        const batch = writeBatch(db);
        chunk.forEach(userDoc => {
          const userRef = doc(db, 'users', userDoc.id);
          batch.update(userRef, { 
            goldBalance: 5000,
            gold: 5000
          });
        });
        await batch.commit();
        updatedCount += chunk.length;
      }

      toast.success(`Successfully granted 5000 gold to ${updatedCount} users!`);

      // 📝 COMPLIANCE: Log this high-impact economy action
      try {
        const { getAuthSafe, getDbSafe } = await import('../services/firebaseConfig');
        const { collection: col, addDoc: aDoc, serverTimestamp, getDoc, doc: dRef } = await import('firebase/firestore');
        const auth2 = await getAuthSafe();
        const db2 = await getDbSafe();
        const actorUid = auth2.currentUser?.uid || 'unknown';
        
        let actorUsername = 'Unknown';
        if (actorUid !== 'unknown') {
          const actorProfile = await getDoc(dRef(db2, 'users', actorUid));
          if (actorProfile.exists()) actorUsername = actorProfile.data().username || actorProfile.data().name || 'Unknown';
        }

        await aDoc(col(db2, 'moderation_logs'), {
          actorId: actorUid,
          actorUsername,
          actionType: 'global_gold_injection',
          targetUserId: 'ALL',
          reason: `Granted 5000 gold to all ${updatedCount} users (Economy panel)`,
          timestamp: serverTimestamp(),
        });
      } catch (logErr) {
        console.error('Failed to write moderation log for gold injection:', logErr);
      }

      fetchDashboardStats();
    } catch (error) {
      console.error("Failed to grant gold:", error);
      toast.error("Failed to grant gold. Check console.");
    } finally {
      setIsUpdatingGold(false);
    }
  };

  const handleCleanupLegacyAccounts = async () => {
    const launchDate = new Date('2026-02-14T00:00:00');
    const excludedUsernames = ['draymond', 'suparo'];

    if (!window.confirm(`WARNING: This will PERMANENTLY DELETE all accounts created before ${launchDate.toLocaleDateString()}. \nExcluded: ${excludedUsernames.join(', ')}. \n\nAre you absolutely sure?`)) return;
    
    setIsCleaningUp(true);
    try {
      const users = await UserService.fetchAllUsers();
      
      const targets = users.filter(user => {
        const username = user.username?.replace('@', '').toLowerCase();
        if (excludedUsernames.includes(username || '')) return false;
        
        let created;
        if ((user as any).createdAt?.toDate) {
            created = (user as any).createdAt.toDate();
        } else if (user.createdAt) {
            created = new Date(user.createdAt);
        } else {
            return true;
        }
        
        return created < launchDate;
      });

      if (targets.length === 0) {
        toast.info("No legacy accounts found to cleanup.");
        setIsCleaningUp(false);
        return;
      }

      if (!window.confirm(`Found ${targets.length} target accounts. Proceed with purge?`)) {
        setIsCleaningUp(false);
        return;
      }

      let successCount = 0;
      for (const target of targets) {
        try {
          await AdminService.deleteAccountAsModerator(String(target.id), 'Legacy account cleanup');
          successCount++;
        } catch (e) {
          console.error(`Failed to purge ${target.username}:`, e);
        }
      }

      toast.success(`Successfully purged ${successCount} legacy accounts!`);
      fetchDashboardStats();
    } catch (error) {
      console.error("Cleanup failed:", error);
      toast.error("Cleanup failed. Check console.");
    } finally {
      setIsCleaningUp(false);
    }
  };

  async function fetchUsersForAudit() {
    setIsLoadingUsers(true);
    try {
      const { UserService } = await import('../services/api');
      const users = await UserService.fetchAllUsers();
      setAllUsers(users);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      toast.error("Failed to load users for audit");
    } finally {
      setIsLoadingUsers(false);
    }
  }

  const handleBulkDelete = async () => {
    if (userProfile?.rank !== 'legion') { toast.error('Unauthorized'); return; }
    if (selectedUserIds.length === 0) {
      toast.error("No users selected");
      return;
    }

    if (!window.confirm(`Are you sure you want to PERMANENTLY PURGE ${selectedUserIds.length} accounts? This action cannot be undone.`)) return;

    setIsCleaningUp(true);
    try {
      let successCount = 0;
      let failCount = 0;
      
      for (const userId of selectedUserIds) {
        try {
          console.log(`[handleBulkDelete] Purging user: ${userId}`);
          await AdminService.deleteAccountAsModerator(userId, 'Bulk purge from Moderator Dashboard');
          successCount++;
        } catch (e: any) {
          console.error(`[handleBulkDelete] Failed to purge user ${userId}:`, e);
          failCount++;
        }
      }
      
      if (failCount > 0) {
        toast.warning(`Purge completed with issues: ${successCount} successful, ${failCount} failed. Check console for details.`);
      } else {
        toast.success(`Successfully purged all ${successCount} users!`);
      }

      // 📝 COMPLIANCE: Log this high-impact bulk admin action
      try {
        const { getAuthSafe, getDbSafe } = await import('../services/firebaseConfig');
        const { collection: col, addDoc: aDoc, serverTimestamp, getDoc, doc: dRef } = await import('firebase/firestore');
        const auth2 = await getAuthSafe();
        const db2 = await getDbSafe();
        const actorUid = auth2.currentUser?.uid || 'unknown';

        let actorUsername = 'Unknown';
        if (actorUid !== 'unknown') {
          const actorProfile = await getDoc(dRef(db2, 'users', actorUid));
          if (actorProfile.exists()) actorUsername = actorProfile.data().username || actorProfile.data().name || 'Unknown';
        }

        await aDoc(col(db2, 'moderation_logs'), {
          actorId: actorUid,
          actorUsername,
          actionType: 'bulk_account_purge',
          targetUserId: selectedUserIds.join(','),
          reason: `Bulk purge: ${successCount} succeeded, ${failCount} failed`,
          timestamp: serverTimestamp(),
        });
      } catch (logErr) {
        console.error('Failed to write moderation log for bulk delete:', logErr);
      }
      
      setSelectedUserIds([]);
      fetchUsersForAudit();
      fetchDashboardStats();
    } catch (error: any) {
      console.error("Bulk delete critical failure:", error);
      toast.error(`Critical Failure: ${error.message || 'Unknown error'}`);
    } finally {
      setIsCleaningUp(false);
    }
  };

  const categories: { id: DashboardCategory, icon: any, label: string }[] = [
    { id: 'Dashboard', icon: TrendingUp, label: 'Dashboard' },
    { id: 'Economy', icon: Coins, label: 'Economy' },
    { id: 'Audit', icon: Gavel, label: 'Audit' },
    { id: 'TotalUsers', icon: ShieldCheck, label: 'Total Users' },
    { id: 'Safety', icon: AlertTriangle, label: 'Safety' },
    { id: 'Analytics', icon: BarChart, label: 'Analytics' },
  ];

  const dashboardStats = [
    { label: 'Total Users', value: stats.totalUsers, change: 'Lifetime', icon: Users, color: 'from-[#F39C12] to-[#E74C3C]' },
    { label: 'Active Yaps', value: stats.activeYaps, change: 'All Time', icon: MessageCircle, color: 'from-[#E74C3C] to-[#F39C12]' },
    { label: 'Pending Reports', value: stats.pendingReports, change: 'Requires Action', icon: AlertTriangle, color: 'from-[#2C3E50] to-[#F39C12]' },
  ];


  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-10000000 bg-black/80 backdrop-blur-sm"
          />

          {/* Modal Container */}
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className={`fixed inset-0 md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 z-10000001 md:w-full md:max-w-2xl h-full md:h-auto max-h-[95vh] md:max-h-[90vh] rounded-3xl shadow-2xl flex flex-col overflow-hidden ${
              theme === 'dark' ? 'bg-light-pattern dark:bg-[#121212] border border-white/10' : 'bg-light-pattern border border-black/10'
            }`}
          >
            <div className={`absolute inset-0 ${theme === 'dark' ? 'bg-linear-to-br from-[#000000] via-[#2C3E50]/20 to-[#000000]' : 'bg-linear-to-br from-[#FAF8F5] via-[#F39C12]/5 to-[#FAF8F5]'} pointer-events-none`} />
            
            {/* Header */}
            <header className={`p-6 border-b ${theme === 'dark' ? 'border-white/10 bg-black/80' : 'border-black/10 bg-white/80'} backdrop-blur-xl relative z-10 shrunk-header shrink-0`}>
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={onClose}
                    className={`p-2 rounded-full ${theme === 'dark' ? 'bg-white/5 hover:bg-white/10 border-white/10' : 'bg-black/5 hover:bg-black/10 border-black/10'} transition-colors border`}
                  >
                    <ArrowLeft className={`w-6 h-6 ${theme === 'dark' ? 'text-white' : 'text-black'}`} />
                  </motion.button>
                  <div>
                    <h1 className={`text-xl md:text-2xl font-bold bg-linear-to-r ${theme === 'dark' ? 'from-white to-white/60' : 'from-black to-black/60'} bg-clip-text text-transparent`}>
                      {categories.find(c => c.id === activeCategory)?.label}
                    </h1>
                    <p className={`text-[10px] ${theme === 'dark' ? 'text-white/40' : 'text-black/40'} uppercase tracking-widest`}>Moderator Dashboard</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                   <motion.button
                     whileTap={{ scale: 0.9 }}
                     onClick={fetchDashboardStats}
                     className={`p-2 rounded-full ${theme === 'dark' ? 'bg-white/5 hover:bg-white/10 border-white/10' : 'bg-black/5 hover:bg-black/10 border-black/10'} transition-colors border hidden sm:flex`}
                   >
                     <RefreshCw className={`w-4 h-4 ${theme === 'dark' ? 'text-white/60' : 'text-black/60'} ${isLoadingStats ? 'animate-spin' : ''}`} />
                   </motion.button>
                   <Avatar className={`w-8 h-8 border ${theme === 'dark' ? 'border-white/20' : 'border-black/20'}`}>
                     <AvatarFallback className={`${theme === 'dark' ? 'bg-[#2C3E50] text-white' : 'bg-[#FAF8F5] text-black'} text-[10px]`}>AD</AvatarFallback>
                   </Avatar>
                </div>
              </div>

              {/* Horizontal Navigation */}
              <div className="w-full mt-6 -mx-6 px-6 overflow-x-auto custom-scrollbar-hide overscroll-x-contain">
                <div className="flex w-max items-center gap-2 pb-3">
                  {categories.map((cat) => (
                    <motion.button
                      key={cat.id}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        setActiveCategory(cat.id);
                        if (cat.id === 'Audit') fetchUsersForAudit();
                      }}
                      className={`px-4 py-2 rounded-xl whitespace-nowrap text-xs font-bold transition-all border ${
                        activeCategory === cat.id
                          ? cat.id === 'Audit'
                            ? `bg-linear-to-r from-[#F39C12] to-[#E74C3C] border-[#F39C12] text-white shadow-lg shadow-[#F39C12]/20`
                            : `bg-linear-to-r ${theme === 'dark' ? 'from-[#4478ea] to-[#3498db]' : 'from-[#F39C12] to-[#E74C3C]'} border-primary text-white`
                          : `${theme === 'dark' ? 'bg-white/5 border-white/10 text-white/40 hover:bg-white/10' : 'bg-black/5 border-black/10 text-black/40 hover:bg-black/10'}`
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <cat.icon className="w-3.5 h-3.5" />
                        {cat.label}
                      </div>
                    </motion.button>
                  ))}
                </div>
              </div>
            </header>

            {/* Content Area - Using standard div with overflow for reliable native scrolling */}
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 custom-scrollbar relative z-10 overscroll-y-contain">
              <div>
                {activeCategory === 'Dashboard' && (
              <div className="space-y-6">
                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {dashboardStats.map((stat, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Card className={`${theme === 'dark' ? 'bg-[#2C3E50]/30 border-white/10' : 'bg-white border-black/10'} overflow-hidden backdrop-blur-sm hover:border-[#F39C12]/30 transition-colors group relative`}>
                        <div className={`absolute top-0 left-0 w-1 h-full bg-linear-to-b ${theme === 'dark' ? 'from-[#4478ea] to-[#3498db]' : stat.color}`} />
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-2">
                             <stat.icon className={`w-5 h-5 ${theme === 'dark' ? 'text-white/40' : 'text-black/40'}`} />
                             <Badge className={`${theme === 'dark' ? 'bg-white/5 text-white/40' : 'bg-black/5 text-black/40'} border-0 text-[9px] uppercase font-bold tracking-tighter`}>
                               {stat.change}
                             </Badge>
                          </div>
                          <p className={`text-[10px] font-bold ${theme === 'dark' ? 'text-white/40' : 'text-black/40'} uppercase tracking-widest mb-1`}>{stat.label}</p>
                          <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-black'}`}>{stat.value}</p>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card className={`${theme === 'dark' ? 'bg-[#2C3E50]/30 border-white/10' : 'bg-white border-black/10'} backdrop-blur-sm`}>
                    <CardHeader>
                      <CardTitle className={`${theme === 'dark' ? 'text-white' : 'text-black'} flex items-center gap-2`}>
                        <TrendingUp className="w-5 h-5 text-[#F39C12]" />
                        Growth Over Time
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                       <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={analyticsData}>
                          <CartesianGrid strokeDasharray="3 3" stroke={theme === 'dark' ? '#ffffff05' : '#00000005'} />
                          <XAxis dataKey="name" stroke={theme === 'dark' ? '#ffffff40' : '#00000040'} fontSize={10} />
                          <YAxis stroke={theme === 'dark' ? '#ffffff40' : '#00000040'} fontSize={10} />
                          <Tooltip
                            contentStyle={{ 
                              backgroundColor: theme === 'dark' ? '#1a1a1a' : '#ffffff', 
                              border: `1px solid ${theme === 'dark' ? '#ffffff10' : '#00000010'}`, 
                              borderRadius: '12px',
                              color: theme === 'dark' ? '#ffffff' : '#000000'
                            }}
                            labelStyle={{ color: theme === 'dark' ? '#ffffff60' : '#00000060' }}
                          />
                          <Line type="monotone" dataKey="users" stroke={theme === 'dark' ? "#4478ea" : "#F39C12"} strokeWidth={3} dot={{ r: 4, fill: theme === 'dark' ? "#4478ea" : "#F39C12" }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                <div className="grid grid-cols-1 gap-6">
                  <Card className={`${theme === 'dark' ? 'bg-[#2C3E50]/30 border-white/10' : 'bg-white border-black/10'} backdrop-blur-sm`}>
                    <CardHeader>
                      <CardTitle className={`${theme === 'dark' ? 'text-white' : 'text-black'} text-lg flex items-center gap-2`}>
                        <AlertTriangle className="w-5 h-5 text-[#E74C3C]" />
                        Recent Safety Alerts
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {realReports.length === 0 ? (
                        <div className={`p-8 text-center ${theme === 'dark' ? 'text-white/20' : 'text-black/20'} text-sm`}>No pending reports found.</div>
                      ) : (
                        realReports.slice(0, 3).map((item: any) => (
                          <div key={item.id} className={`flex items-center gap-4 p-4 rounded-xl ${theme === 'dark' ? 'bg-white/5 border-white/5 hover:bg-white/10' : 'bg-black/5 border-black/5 hover:bg-black/10'} transition-colors`}>
                            <div className={`w-10 h-10 rounded-full ${theme === 'dark' ? 'bg-white/5 border-white/10' : 'bg-black/5 border-black/10'} flex items-center justify-center border`}>
                              <AlertTriangle className="w-5 h-5 text-[#E74C3C]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-bold ${theme === 'dark' ? 'text-white' : 'text-black'} truncate`}>Reported {item.targetType}</p>
                              <p className={`text-xs ${theme === 'dark' ? 'text-white/40' : 'text-black/40'} truncate`}>{item.reason || item.category}</p>
                            </div>
                            <Badge className="bg-[#E74C3C]/20 text-[#E74C3C] border-0 text-[10px] font-bold">NEW</Badge>
                          </div>
                        ))
                      )}
                      <Button variant="ghost" className={`w-full text-xs ${theme === 'dark' ? 'text-white/40 hover:text-white' : 'text-black/40 hover:text-black'} mt-2`} onClick={() => setActiveCategory('Safety')}>
                        View all reports
                      </Button>
                    </CardContent>
                  </Card>
                </div>
                </div>
              </div>
            )}

            {activeCategory === 'Economy' && (
              <div className="space-y-6">
                <Card className={`${theme === 'dark' ? 'bg-[#2C3E50]/30 border-white/10' : 'bg-white border-black/10'} backdrop-blur-sm`}>
                  <CardHeader>
                    <CardTitle className={theme === 'dark' ? 'text-white' : 'text-black'}>Gold Management</CardTitle>
                    <CardDescription className={theme === 'dark' ? 'text-white/40' : 'text-black/40'}>Tools for managing user currency and market balance</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="p-6 rounded-2xl bg-yellow-500/5 border border-yellow-500/10 flex items-center justify-between">
                      <div className="space-y-1">
                        <h4 className="text-lg font-bold text-yellow-500">Global Gold Injection</h4>
                        <p className={`text-sm ${theme === 'dark' ? 'text-white/40' : 'text-black/40'} max-w-md`}>Instantly grant 5,000 gold to every user in the system. Use this for server-wide apologies or special events.</p>
                      </div>
                      <Button 
                        onClick={handleGrantGoldToAll}
                        disabled={isUpdatingGold}
                        className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold h-12 px-8 rounded-xl shadow-lg shadow-yellow-500/20"
                      >
                        {isUpdatingGold ? "Processing..." : "Grant 5000 Gold"}
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className={`p-4 rounded-xl ${theme === 'dark' ? 'bg-white/5 border-white/10' : 'bg-black/5 border-black/10'} border text-center space-y-2`}>
                        <p className={`text-xs ${theme === 'dark' ? 'text-white/40' : 'text-black/40'} uppercase tracking-wider`}>Avg User Balance</p>
                        <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-black'}`}>1,240 <span className="text-xs text-yellow-500">Au</span></p>
                      </div>
                      <div className={`p-4 rounded-xl ${theme === 'dark' ? 'bg-white/5 border-white/10' : 'bg-black/5 border-black/10'} border text-center space-y-2`}>
                        <p className={`text-xs ${theme === 'dark' ? 'text-white/40' : 'text-black/40'} uppercase tracking-wider`}>Total Market Volume</p>
                        <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-black'}`}>45.2M <span className="text-xs text-yellow-500">Au</span></p>
                      </div>
                      <div className={`p-4 rounded-xl ${theme === 'dark' ? 'bg-white/5 border-white/10' : 'bg-black/5 border-black/10'} border text-center space-y-2`}>
                        <p className={`text-xs ${theme === 'dark' ? 'text-white/40' : 'text-black/40'} uppercase tracking-wider`}>New Gold Today</p>
                        <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-black'}`}>+8.5K <span className="text-xs text-yellow-500">Au</span></p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeCategory === 'TotalUsers' && (
              <div className="space-y-6">
                <Card className={`${theme === 'dark' ? 'bg-[#2C3E50]/30 border-white/10' : 'bg-white border-black/10'} backdrop-blur-sm`}>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-7">
                    <div>
                      <CardTitle className={`${theme === 'dark' ? 'text-white' : 'text-black'} text-2xl`}>Bulk User Management</CardTitle>
                      <CardDescription className={theme === 'dark' ? 'text-white/40' : 'text-black/40'}>Select users one-by-one to perform bulk administrative actions</CardDescription>
                    </div>
                    <div className="flex items-center gap-3">
                      {selectedUserIds.length > 0 && (
                        <motion.div initial={{ scale: 0.8 }} animate={{ scale: 1 }} className="flex items-center gap-3">
                           <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => setSelectedUserIds([])}
                            className={`${theme === 'dark' ? 'text-white/40 hover:text-white' : 'text-black/40 hover:text-black'}`}
                          >
                            Clear Selection
                          </Button>
                          <Button 
                            variant="destructive" 
                            size="sm"
                            onClick={handleBulkDelete}
                            disabled={isCleaningUp}
                            className="bg-red-500/20 hover:bg-red-500 text-red-500 hover:text-white border border-red-500/30"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Purge {selectedUserIds.length} Accounts
                          </Button>
                        </motion.div>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={fetchUsersForAudit}
                        disabled={isLoadingUsers}
                        className={`${theme === 'dark' ? 'bg-white/5 border-white/10 text-white' : 'bg-black/5 border-black/10 text-black'}`}
                      >
                        <Search className="w-4 h-4 mr-2" />
                        Reload Registry
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-4 mb-6">
                      <div className="relative flex-1">
                        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${theme === 'dark' ? 'text-white/40' : 'text-black/40'}`} />
                        <Input 
                          placeholder="Search by name, username or ID..." 
                          className={`pl-10 ${theme === 'dark' ? 'bg-white/5 border-white/10 text-white' : 'bg-black/5 border-black/10 text-black'} placeholder:${theme === 'dark' ? 'text-white/20' : 'text-black/20'}`}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className={`rounded-xl border ${theme === 'dark' ? 'border-white/10' : 'border-black/10'} overflow-hidden`}>
                        <div className={`grid grid-cols-1 divide-y ${theme === 'dark' ? 'divide-white/5 bg-black/20' : 'divide-black/5 bg-white/20'}`}>
                          {isLoadingUsers ? (
                            <div className="p-20 text-center space-y-4">
                              <div className="w-10 h-10 border-4 border-[#F39C12] border-t-transparent rounded-full animate-spin mx-auto" />
                              <p className="text-sm text-white/40">Fetching user registry...</p>
                            </div>
                          ) : (
                            allUsers
                              .filter(user => {
                                const search = searchQuery.toLowerCase();
                                return (
                                  user.name?.toLowerCase().includes(search) ||
                                  user.username?.toLowerCase().includes(search) ||
                                  String(user.id).includes(search)
                                );
                              })
                              .slice()
                              .sort((a: any, b: any) => {
                                // Newest accounts first. Users with no createdAt
                                // sink to the bottom (treated as the epoch).
                                const toMs = (u: any) => {
                                  if (!u?.createdAt) return 0;
                                  if (u.createdAt.toMillis) return u.createdAt.toMillis();
                                  if (u.createdAt.toDate) return u.createdAt.toDate().getTime();
                                  const d = new Date(u.createdAt);
                                  return isNaN(d.getTime()) ? 0 : d.getTime();
                                };
                                return toMs(b) - toMs(a);
                              })
                              .map((user) => (
                                <div 
                                  key={user.id} 
                                  className={`group flex items-center gap-4 p-4 transition-colors ${
                                    selectedUserIds.includes(String(user.id)) 
                                      ? (theme === 'dark' ? 'bg-[#F39C12]/10' : 'bg-[#F39C12]/20') 
                                      : (theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-black/5')
                                  }`}
                                  onClick={() => {
                                    const id = String(user.id);
                                    setSelectedUserIds(prev => 
                                      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
                                    );
                                  }}
                                >
                                  <Checkbox 
                                    checked={selectedUserIds.includes(String(user.id))}
                                    className={`${theme === 'dark' ? 'border-white/20' : 'border-black/20'} data-[state=checked]:bg-[#F39C12] data-[state=checked]:border-[#F39C12]`}
                                  />
                                  <Avatar className={`w-10 h-10 border ${theme === 'dark' ? 'border-white/10' : 'border-black/10'}`}>
                                    <AvatarImage src={user.avatar || user.image} />
                                    <AvatarFallback className={`${theme === 'dark' ? 'bg-white/10 text-white/40' : 'bg-black/10 text-black/40'}`}>{user.name?.[0]}</AvatarFallback>
                                  </Avatar>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <p className={`text-sm font-bold ${theme === 'dark' ? 'text-white' : 'text-black'} truncate`}>{user.name}</p>
                                      <Badge variant="outline" className={`text-[9px] h-4 ${theme === 'dark' ? 'border-white/10 text-white/40' : 'border-black/10 text-black/40'} font-mono`}>
                                        ID: {String(user.id).slice(0, 6)}...
                                      </Badge>
                                    </div>
                                    <p className={`text-xs ${theme === 'dark' ? 'text-white/40' : 'text-black/40'} truncate text-mono`}>{user.username}</p>
                                  </div>
                                  <div className="text-right shrink-0">
                                    <p className={`text-[10px] ${theme === 'dark' ? 'text-white/20' : 'text-black/20'} uppercase tracking-tighter mb-0.5`}>Joined</p>
                                    <p className={`text-xs ${theme === 'dark' ? 'text-white/60' : 'text-black/60'}`}>
                                      {user.createdAt ? new Date((user as any).createdAt?.toDate ? (user as any).createdAt.toDate() : user.createdAt).toLocaleDateString() : 'Unknown'}
                                    </p>
                                  </div>
                                  <motion.div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Button size="sm" variant="ghost" className={`h-8 w-8 p-0 ${theme === 'dark' ? 'text-white/20 hover:text-white' : 'text-black/20 hover:text-black'}`}>
                                      <MoreHorizontal className="w-4 h-4" />
                                    </Button>
                                  </motion.div>
                                </div>
                              ))
                          )}
                          {!isLoadingUsers && allUsers.length === 0 && (
                            <div className="p-20 text-center text-white/20">
                              No users found matching your criteria.
                            </div>
                          )}
                        </div>
                    </div>
                    <div className={`mt-4 p-4 rounded-xl ${theme === 'dark' ? 'bg-black/40 border-white/10' : 'bg-white border-black/10'} flex items-center justify-between`}>
                      <div className="flex items-center gap-6">
                        <div className="space-y-1">
                          <p className={`text-[10px] uppercase font-bold ${theme === 'dark' ? 'text-white/20' : 'text-black/20'} tracking-widest`}>Selected Users</p>
                          <p className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-black'} leading-none`}>{selectedUserIds.length}</p>
                        </div>
                        <div className={`w-px h-8 ${theme === 'dark' ? 'bg-white/10' : 'bg-black/10'}`} />
                        <div className="space-y-1">
                          <p className={`text-[10px] uppercase font-bold ${theme === 'dark' ? 'text-white/20' : 'text-black/20'} tracking-widest`}>Total Registry</p>
                          <p className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-black'} leading-none`}>{allUsers.length}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                         <div className="flex -space-x-3 mr-4">
                            {selectedUserIds.slice(0, 5).map(id => {
                              const user = allUsers.find(u => String(u.id) === id);
                              return (
                                <Avatar key={id} className={`w-8 h-8 ring-2 ${theme === 'dark' ? 'ring-[#000]' : 'ring-white'}`}>
                                  <AvatarImage src={user?.avatar || user?.image} />
                                  <AvatarFallback className={`${theme === 'dark' ? 'bg-[#2C3E50] text-white' : 'bg-[#FAF8F5] text-black'} text-[10px]`}>{user?.name?.[0]}</AvatarFallback>
                                </Avatar>
                              );
                            })}
                            {selectedUserIds.length > 5 && (
                              <div className={`w-8 h-8 rounded-full ${theme === 'dark' ? 'bg-white/10 text-white border-black' : 'bg-black/10 text-black border-white'} border-2 flex items-center justify-center text-[10px]`}>
                                +{selectedUserIds.length - 5}
                              </div>
                            )}
                         </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeCategory === 'Safety' && (
              <div className="space-y-6">
                <Card className={`${theme === 'dark' ? 'bg-[#2C3E50]/30 border-white/10' : 'bg-white border-black/10'} backdrop-blur-sm`}>
                  <CardHeader>
                    <CardTitle className={`${theme === 'dark' ? 'text-white' : 'text-black'} flex items-center gap-2`}>
                      <AlertTriangle className="w-5 h-5 text-[#E74C3C]" />
                      Report Moderation Queue
                    </CardTitle>
                    <CardDescription className={theme === 'dark' ? 'text-white/40' : 'text-black/40'}>Review and resolve community reports one item at a time</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {realReports.length === 0 ? (
                        <div className="p-20 text-center space-y-4">
                          <CheckCircle className="w-12 h-12 text-green-500 mx-auto opacity-20" />
                          <p className="text-white/20 text-sm">Moderation queue is empty. Good job!</p>
                        </div>
                      ) : (
                        realReports.map((item) => (
                            <div
                              key={item.id}
                              className={`p-5 rounded-2xl ${theme === 'dark' ? 'bg-black/40 border-white/10 hover:border-white/20' : 'bg-white border-black/10 hover:border-black/20'} border flex flex-col gap-4 group transition-all`}
                            >
                              <div className="flex items-start gap-4">
                                <div className="relative">
                                  <div className={`w-12 h-12 rounded-full ${theme === 'dark' ? 'bg-white/5 border-white/10' : 'bg-black/5 border-black/10'} flex items-center justify-center border`}>
                                    <AlertTriangle className="w-6 h-6 text-[#E74C3C]" />
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-3 mb-1">
                                    <h4 className={`text-sm font-bold ${theme === 'dark' ? 'text-white' : 'text-black'}`}>Report #{String(item.id).slice(-4)}</h4>
                                    <Badge className={`${theme === 'dark' ? 'bg-white/5 text-white/60 border-white/10' : 'bg-black/5 text-black/60 border-black/10'} text-[9px] uppercase`}>{item.targetType}</Badge>
                                  </div>
                                  <p className={`text-xs ${theme === 'dark' ? 'text-white/80' : 'text-black/80'} line-clamp-2`}>Reason: {item.reason || item.category}</p>
                                  <p className={`text-[10px] ${theme === 'dark' ? 'text-white/40' : 'text-black/40'} mt-1`}>Reporter: {item.reporterUsername || item.reporterId}</p>
                                </div>
                              </div>
                            
                            <div className="flex gap-2">
                              <Button
                                className="flex-1 bg-green-500/20 hover:bg-green-500 text-green-500 hover:text-white border border-green-500/30 font-bold h-10 rounded-xl transition-all"
                                onClick={() => handleUpdateReportStatus(item.id, 'dismissed')}
                              >
                                <UserCheck className="w-4 h-4 mr-2" />
                                Dismiss
                              </Button>
                              <Button
                                className="flex-1 bg-red-500/20 hover:bg-red-500 text-red-500 hover:text-white border border-red-500/30 font-bold h-10 rounded-xl transition-all"
                                onClick={() => handleUpdateReportStatus(item.id, 'resolved')}
                              >
                                <UserX className="w-4 h-4 mr-2" />
                                Resolve
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeCategory === 'Audit' && (
              <div className="space-y-6">
                <Card className={`${theme === 'dark' ? 'bg-[#2C3E50]/30 border-white/10' : 'bg-white border-black/10'} backdrop-blur-sm`}>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-7">
                    <div>
                      <CardTitle className={`${theme === 'dark' ? 'text-white' : 'text-black'} text-2xl`}>Watcher Action Log</CardTitle>
                      <CardDescription className={theme === 'dark' ? 'text-white/40' : 'text-black/40'}>Real-time monitoring of all moderation and administrative actions</CardDescription>
                    </div>
                    <Button 
                        variant="outline" 
                        size="sm" 
                        className={`${theme === 'dark' ? 'border-white/10 text-white/60 hover:bg-white/5' : 'border-black/10 text-black/60 hover:bg-black/5'} h-10 px-4`}
                        onClick={fetchLogs}
                      >
                        <RefreshCw className={`w-4 h-4 mr-2 ${isLoadingLogs ? 'animate-spin' : ''}`} />
                        Refresh Logs
                      </Button>
                  </CardHeader>
                  <CardContent>
                    <div className={`rounded-2xl border ${theme === 'dark' ? 'bg-black/20 border-white/5' : 'bg-gray-50/50 border-black/5'}`}>
                      <div className="max-h-[500px] overflow-y-auto custom-scrollbar">
                        {isLoadingLogs ? (
                          <div className="p-20 text-center text-white/20">Loading audit logs...</div>
                        ) : moderationLogs.length === 0 ? (
                          <div className="p-20 text-center text-white/20">No actions logged in the registry.</div>
                        ) : (
                          <div className="divide-y divide-white/5">
                            {moderationLogs.map((log) => (
                              <div key={log.id} className="p-4 hover:bg-white/5 transition-colors group border-l-2 border-transparent hover:border-[#F39C12]">
                                <div className="flex items-start gap-4">
                                  <div className={`mt-1 w-10 h-10 rounded-full flex items-center justify-center border shrink-0 ${
                                    log.actionType?.includes('purge') || log.actionType?.includes('delete') || log.actionType?.includes('ban') ? 'bg-red-500/10 border-red-500/20 text-red-500' :
                                    log.actionType?.includes('gold') ? 'bg-yellow-500/10 border-yellow-500/20 text-yellow-500' :
                                    log.actionType?.includes('rank') ? 'bg-blue-500/10 border-blue-500/20 text-blue-500' :
                                    'bg-gray-500/10 border-white/10 text-white/40'
                                  }`}>
                                    {log.actionType?.includes('gold') ? <Coins className="w-5 h-5" /> : 
                                     log.actionType?.includes('delete') ? <Trash2 className="w-5 h-5" /> :
                                     log.actionType?.includes('ban') ? <UserX className="w-5 h-5" /> :
                                     log.actionType?.includes('rank') ? <ShieldCheck className="w-5 h-5" /> :
                                     <Gavel className="w-5 h-5" />}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center flex-wrap gap-2 mb-1">
                                      <p className={`text-sm font-bold ${theme === 'dark' ? 'text-white' : 'text-black'}`}>
                                        {String(log.actionType || 'Action').replace(/_/g, ' ').toUpperCase()}
                                      </p>
                                      <Badge variant="outline" className={`text-[10px] px-2 py-0 h-4 ${theme === 'dark' ? 'bg-white/5 text-white/80' : 'bg-black/5 text-black/80'}`}>
                                        {getModName(log)}
                                      </Badge>
                                      {log.targetUsername && (
                                        <div className="flex items-center gap-1">
                                          <span className="text-[10px] text-white/20">➔</span>
                                          <Badge variant="secondary" className="text-[10px] px-2 py-0 h-4 bg-[#F39C12]/10 text-[#F39C12] border-none">
                                            {formatUsername(log.targetUsername)}
                                          </Badge>
                                        </div>
                                      )}
                                      {!log.targetUsername && (log.targetId || log.targetUserId) && (log.targetId !== 'ALL' && log.targetUserId !== 'ALL') && (
                                        <div className="flex items-center gap-1">
                                          <span className="text-[10px] text-white/20">➔</span>
                                          <span className="text-[10px] font-mono opacity-40">ID:{String(log.targetId || log.targetUserId).slice(0, 6)}</span>
                                        </div>
                                      )}
                                    </div>
                                    <p className={`text-xs ${theme === 'dark' ? 'text-white/60' : 'text-black/60'} leading-relaxed`}>
                                      {log.reason || 'No specific reason provided.'}
                                    </p>

                                    {/* Expanded Details for Yap Deletions */}
                                    {(log.actionType?.includes('delete') || log.actionType?.includes('purge')) && log.metadata?.content && (
                                      <div className={`mt-2 p-2 rounded-lg border text-[10px] italic ${theme === 'dark' ? 'bg-black/20 border-white/5 text-white/40' : 'bg-black/5 border-black/5 text-black/40'}`}>
                                        "{log.metadata.content.length > 100 ? log.metadata.content.slice(0, 100) + '...' : log.metadata.content}"
                                      </div>
                                    )}

                                    {log.actionType === 'rank_change' && log.beforeState?.rank && (
                                      <div className="mt-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest opacity-40">
                                        <span>{log.beforeState.rank}</span>
                                        <ArrowLeft className="w-3 h-3 rotate-180" />
                                        <span className="text-[#F39C12]">{log.afterState?.rank}</span>
                                      </div>
                                    )}
                                  </div>
                                  <div className="text-right shrink-0">
                                    <p className={`text-[10px] font-mono ${theme === 'dark' ? 'text-white/20' : 'text-black/20'}`}>
                                      {formatAuditDate(log.timestamp)}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeCategory === 'Analytics' && (
              <div className="space-y-6 pb-20">
                {/* ── Scale signals ──────────────────────────────────────────
                    Each card answers one open pricing/window question with real
                    numbers instead of an assumption about how busy the app is. */}
                <Card className={`${theme === 'dark' ? 'bg-[#2C3E50]/30 border-white/10' : 'bg-white border-black/10'} backdrop-blur-sm`}>
                  <CardHeader>
                    <CardTitle className={theme === 'dark' ? 'text-white' : 'text-black'}>Scale Signals</CardTitle>
                    <CardDescription className={theme === 'dark' ? 'text-white/50' : 'text-black/50'}>
                      Is each limit sized for the app that exists today?
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {isLoadingSignals && !scaleSignals && (
                      <p className={`text-sm ${theme === 'dark' ? 'text-white/50' : 'text-black/50'}`}>Computing…</p>
                    )}

                    {scaleSignals && (
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

                        {/* 1. Community health → creation price */}
                        <div className={`rounded-xl p-4 ${theme === 'dark' ? 'bg-black/20' : 'bg-black/5'}`}>
                          <div className="flex items-center gap-2 mb-3">
                            <Users className="w-4 h-4 text-[#4478ea]" />
                            <h4 className={`text-sm font-bold ${theme === 'dark' ? 'text-white' : 'text-black'}`}>Community Health</h4>
                          </div>
                          <div className={`text-3xl font-bold mb-1 ${theme === 'dark' ? 'text-white' : 'text-black'}`}>
                            {scaleSignals.communities.alivePercent}%
                          </div>
                          <p className={`text-xs mb-3 ${theme === 'dark' ? 'text-white/50' : 'text-black/50'}`}>
                            alive ({scaleSignals.communities.aliveCount} of {scaleSignals.communities.aliveCount + scaleSignals.communities.dormantCount})
                            — 5+ members and active in 30d
                          </p>
                          <dl className="space-y-1 text-xs">
                            <div className="flex justify-between">
                              <dt className={theme === 'dark' ? 'text-white/60' : 'text-black/60'}>Tribes / Forums</dt>
                              <dd className={theme === 'dark' ? 'text-white' : 'text-black'}>
                                {scaleSignals.communities.tribesTotal} / {scaleSignals.communities.forumsTotal}
                              </dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className={theme === 'dark' ? 'text-white/60' : 'text-black/60'}>Median members</dt>
                              <dd className={theme === 'dark' ? 'text-white' : 'text-black'}>{scaleSignals.communities.medianMembers}</dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className={theme === 'dark' ? 'text-white/60' : 'text-black/60'}>Never got going (≤1)</dt>
                              <dd className={theme === 'dark' ? 'text-white' : 'text-black'}>{scaleSignals.communities.emptyCount}</dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className={theme === 'dark' ? 'text-white/60' : 'text-black/60'}>Created last 30d</dt>
                              <dd className={theme === 'dark' ? 'text-white' : 'text-black'}>{scaleSignals.communities.createdLast30d}</dd>
                            </div>
                          </dl>
                          <p className={`mt-3 text-[11px] leading-snug ${theme === 'dark' ? 'text-white/40' : 'text-black/40'}`}>
                            Low alive% ⇒ the 500/1000 gold gate isn't filtering dead groups.
                            Few created ⇒ it's suppressing supply.
                          </p>
                        </div>

                        {/* 2. Discover depth → swipe price */}
                        <div className={`rounded-xl p-4 ${theme === 'dark' ? 'bg-black/20' : 'bg-black/5'}`}>
                          <div className="flex items-center gap-2 mb-3">
                            <Eye className="w-4 h-4 text-[#F39C12]" />
                            <h4 className={`text-sm font-bold ${theme === 'dark' ? 'text-white' : 'text-black'}`}>Discover Depth</h4>
                          </div>
                          <div className={`text-3xl font-bold mb-1 ${theme === 'dark' ? 'text-white' : 'text-black'}`}>
                            ~{scaleSignals.discover.estimatedMedianPool}
                          </div>
                          <p className={`text-xs mb-3 ${theme === 'dark' ? 'text-white/50' : 'text-black/50'}`}>
                            swipeable profiles for a typical user (bracket − friends)
                          </p>
                          <dl className="space-y-1 text-xs">
                            <div className="flex justify-between">
                              <dt className={theme === 'dark' ? 'text-white/60' : 'text-black/60'}>Discoverable</dt>
                              <dd className={theme === 'dark' ? 'text-white' : 'text-black'}>{scaleSignals.discover.discoverableTotal}</dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className={theme === 'dark' ? 'text-white/60' : 'text-black/60'}>Adult / Minor</dt>
                              <dd className={theme === 'dark' ? 'text-white' : 'text-black'}>
                                {scaleSignals.discover.adultPool} / {scaleSignals.discover.minorPool}
                              </dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className={theme === 'dark' ? 'text-white/60' : 'text-black/60'}>Median friends</dt>
                              <dd className={theme === 'dark' ? 'text-white' : 'text-black'}>{scaleSignals.discover.medianFriends}</dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className={theme === 'dark' ? 'text-white/60' : 'text-black/60'}>Opted out</dt>
                              <dd className={theme === 'dark' ? 'text-white' : 'text-black'}>{scaleSignals.discover.optedOutCount}</dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className={theme === 'dark' ? 'text-white/60' : 'text-black/60'}>Gold to exhaust</dt>
                              <dd className={theme === 'dark' ? 'text-white' : 'text-black'}>{scaleSignals.discover.goldToExhaustPool}</dd>
                            </div>
                          </dl>
                          <p className={`mt-3 text-[11px] leading-snug ${theme === 'dark' ? 'text-white/40' : 'text-black/40'}`}>
                            A shallow pool means users run out of people before they run out of gold —
                            the 10/30 price is rationing something already scarce.
                          </p>
                        </div>

                        {/* 3. Signups → New Yappers window */}
                        <div className={`rounded-xl p-4 ${theme === 'dark' ? 'bg-black/20' : 'bg-black/5'}`}>
                          <div className="flex items-center gap-2 mb-3">
                            <TrendingUp className="w-4 h-4 text-[#2ecc71]" />
                            <h4 className={`text-sm font-bold ${theme === 'dark' ? 'text-white' : 'text-black'}`}>Signup Flow</h4>
                          </div>
                          <div className={`text-3xl font-bold mb-1 ${theme === 'dark' ? 'text-white' : 'text-black'}`}>
                            {scaleSignals.signups.newYappersEligible}
                          </div>
                          <p className={`text-xs mb-3 ${theme === 'dark' ? 'text-white/50' : 'text-black/50'}`}>
                            profiles the “New Yappers This Month” section can show
                          </p>
                          <dl className="space-y-1 text-xs">
                            <div className="flex justify-between">
                              <dt className={theme === 'dark' ? 'text-white/60' : 'text-black/60'}>Signups 7d</dt>
                              <dd className={theme === 'dark' ? 'text-white' : 'text-black'}>{scaleSignals.signups.last7d}</dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className={theme === 'dark' ? 'text-white/60' : 'text-black/60'}>Signups 30d</dt>
                              <dd className={theme === 'dark' ? 'text-white' : 'text-black'}>{scaleSignals.signups.last30d}</dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className={theme === 'dark' ? 'text-white/60' : 'text-black/60'}>Signups 90d</dt>
                              <dd className={theme === 'dark' ? 'text-white' : 'text-black'}>{scaleSignals.signups.last90d}</dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className={theme === 'dark' ? 'text-white/60' : 'text-black/60'}>Days to fill 20 cards</dt>
                              <dd className={theme === 'dark' ? 'text-white' : 'text-black'}>
                                {scaleSignals.signups.daysToFillSection ?? '>all time'}
                              </dd>
                            </div>
                          </dl>
                          <p className={`mt-3 text-[11px] leading-snug ${theme === 'dark' ? 'text-white/40' : 'text-black/40'}`}>
                            If filling 20 cards takes more than 30 days, the section renders half-empty
                            and the window should widen.
                          </p>
                        </div>

                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Growth tools */}
                <div className="flex flex-wrap gap-3">
                  <Button
                    variant="outline"
                    className="flex items-center gap-2 bg-[#4478ea]/10 border-[#4478ea]/30 text-[#4478ea] hover:bg-[#4478ea]/20"
                    onClick={() => window.dispatchEvent(new CustomEvent('openGrowthDashboard'))}
                  >
                    <TrendingUp className="w-4 h-4" />
                    Live Growth Dashboard
                  </Button>
                  <Button
                    variant="outline"
                    className="flex items-center gap-2 bg-[#F39C12]/10 border-[#F39C12]/30 text-[#F39C12] hover:bg-[#F39C12]/20"
                    onClick={() => window.dispatchEvent(new CustomEvent('openAdCreativeStudio'))}
                  >
                    <Eye className="w-4 h-4" />
                    Ad Creative Studio
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card className={`${theme === 'dark' ? 'bg-[#2C3E50]/30 border-white/10' : 'bg-white border-black/10'} backdrop-blur-sm`}>
                      <CardHeader>
                        <CardTitle className={theme === 'dark' ? 'text-white' : 'text-black'}>User Stickiness</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={analyticsData}>
                            <CartesianGrid strokeDasharray="3 3" stroke={theme === 'dark' ? '#ffffff05' : '#00000005'} />
                            <XAxis dataKey="name" stroke={theme === 'dark' ? '#ffffff40' : '#00000040'} fontSize={10} />
                            <YAxis stroke={theme === 'dark' ? '#ffffff40' : '#00000040'} fontSize={10} />
                            <Tooltip
                              contentStyle={{ 
                                backgroundColor: theme === 'dark' ? '#1a1a1a' : '#ffffff', 
                                border: `1px solid ${theme === 'dark' ? '#ffffff10' : '#00000010'}`, 
                                borderRadius: '12px',
                                color: theme === 'dark' ? '#ffffff' : '#000000'
                              }}
                              labelStyle={{ color: theme === 'dark' ? '#ffffff60' : '#00000060' }}
                            />
                            <Bar dataKey="users" fill="#F39C12" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>

                    <Card className={`${theme === 'dark' ? 'bg-[#2C3E50]/30 border-white/10' : 'bg-white border-black/10'} backdrop-blur-sm`}>
                      <CardHeader>
                        <CardTitle className={theme === 'dark' ? 'text-white' : 'text-black'}>Market Activity</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={analyticsData}>
                            <CartesianGrid strokeDasharray="3 3" stroke={theme === 'dark' ? '#ffffff05' : '#00000005'} />
                            <XAxis dataKey="name" stroke={theme === 'dark' ? '#ffffff40' : '#00000040'} fontSize={10} />
                            <YAxis stroke={theme === 'dark' ? '#ffffff40' : '#00000040'} fontSize={10} />
                            <Tooltip
                              contentStyle={{ 
                                backgroundColor: theme === 'dark' ? '#1a1a1a' : '#ffffff', 
                                border: `1px solid ${theme === 'dark' ? '#ffffff10' : '#00000010'}`, 
                                borderRadius: '12px',
                                color: theme === 'dark' ? '#ffffff' : '#000000'
                              }}
                              labelStyle={{ color: theme === 'dark' ? '#ffffff60' : '#00000060' }}
                            />
                            <Bar dataKey="yaps" fill="#E74C3C" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                </div>
              </div>
            )}
            </div>
           </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
